import { lightColors as colors } from "@common/consts/colors";
import { Flavor } from "@common/utils/type-utils";
import type { SelectableId } from "@web/src/app/use-cases/id-flavors";
import { useStores } from "@web/src/hooks/useStore";
import { action, computed, makeAutoObservable, observable } from "mobx";
import * as React from "react";

interface Coordinates {
  x: number;
  y: number;
}
interface SelectArea {
  start: Coordinates;
  end: Coordinates;
}
interface UseAreaSelectionProps {
  container?: React.RefObject<HTMLElement | null> | undefined;
}

export class AreaSelectStore {
  @observable
  tmpSelection = new Set<SelectableId>(); // not committed yet

  @observable
  tmpDeselection = new Set<SelectableId>(); // not committed yet

  @observable
  selection = new Set<SelectableId>(); // currently committed selection

  @observable
  isAreaSelecting = false;

  @observable
  selectableRefs = new Map<SelectableId, React.RefObject<HTMLElement | null>>();

  @observable
  selectionArea: SelectArea | null = null;

  @computed
  get selectionRect() {
    if (!this.selectionArea) return null;
    const { start, end } = this.selectionArea;
    return new DOMRect(
      Math.min(start.x, end.x),
      Math.min(start.y, end.y),
      Math.abs(start.x - end.x),
      Math.abs(start.y - end.y)
    );
  }

  constructor() {
    makeAutoObservable(this);
  }

  @action
  setSelectionArea(area: SelectArea | null) {
    this.selectionArea = area;
  }

  @action
  registerSelectable(id: SelectableId, ref: React.RefObject<HTMLElement | null>) {
    this.selectableRefs.set(id, ref);
  }

  @action
  deregisterSelectable(id: SelectableId) {
    this.selectableRefs.delete(id);
  }

  @action
  setSelection(selection: Set<SelectableId>) {
    this.selection = selection;
  }

  @action
  setTmpSelection(selection: Set<SelectableId>) {
    this.tmpSelection = selection;
  }

  @action
  setTmpDeselection(selection: Set<SelectableId>) {
    this.tmpDeselection = selection;
  }

  @action
  clearSelection() {
    this.selection = new Set();
    this.tmpSelection = new Set();
    this.tmpDeselection = new Set();
  }

  @action
  setIsAreaSelecting(isAreaSelecting: boolean) {
    this.isAreaSelecting = isAreaSelecting;
  }
}

export function useAreaSelection({ container = { current: document.body } }: UseAreaSelectionProps) {
  const { areaSelectStore } = useStores();

  const boxElementRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      const boxNode = document.createElement("div");
      boxNode.className = "z-area-select-box";
      boxNode.style.position = "fixed";
      boxNode.style.background = `${colors["blue-3"]}12`; // alpha 0.5
      boxNode.style.boxShadow = `inset 0 0 0 2px ${colors["blue-3"]}`;
      boxNode.style.borderRadius = "2px";
      boxNode.style.pointerEvents = "none";
      boxElementRef.current = boxNode;
    }

    return () => {
      if (boxElementRef.current) {
        boxElementRef.current.remove();
      }
    };
  }, []);

  const handleMouseMove = React.useCallback(
    (e: MouseEvent) => {
      document.body.style.userSelect = "none";

      if (!areaSelectStore.isAreaSelecting) {
        if (!e.shiftKey) {
          areaSelectStore.clearSelection();
        }

        areaSelectStore.setIsAreaSelecting(true);

        // spawn box
        const containerElement = container.current;
        const selectionBoxElement = boxElementRef.current;
        if (containerElement && selectionBoxElement) {
          if (!document.body.contains(selectionBoxElement)) {
            containerElement.appendChild(selectionBoxElement);
          }
        }
      }

      if (!areaSelectStore.selectionArea?.start || !boxElementRef.current) {
        console.warn("Area selection not started!!");
        return;
      }

      areaSelectStore.setSelectionArea({
        ...areaSelectStore.selectionArea,
        end: {
          x: e.clientX,
          y: e.clientY,
        },
      });

      // check for selection among selectables
      const selectablesIter = areaSelectStore.selectableRefs.entries();
      for (const [selectableId, selectableRef] of selectablesIter) {
        const selectableRect = selectableRef.current?.getBoundingClientRect();
        const boxRect = boxElementRef.current.getBoundingClientRect();
        const isInSelectArea =
          selectableRect &&
          // intersect
          ((selectableRect.left < boxRect.right &&
            selectableRect.right > boxRect.left &&
            selectableRect.top < boxRect.bottom &&
            selectableRect.bottom > boxRect.top) || // or fully contained
            (boxRect.left < selectableRect.left &&
              boxRect.right > selectableRect.right &&
              boxRect.top < selectableRect.top &&
              boxRect.bottom > selectableRect.bottom));

        // get most recent
        const tmpSelection = areaSelectStore.tmpSelection;
        const selection = areaSelectStore.selection;

        if (isInSelectArea && !tmpSelection.has(selectableId)) {
          areaSelectStore.setTmpSelection(new Set(tmpSelection).add(selectableId));
          continue;
        }

        if (isInSelectArea && selection.has(selectableId)) {
          areaSelectStore.setTmpDeselection(new Set(tmpSelection).add(selectableId));
          continue;
        }

        if (!isInSelectArea && tmpSelection.has(selectableId)) {
          const newSelection = new Set(tmpSelection);
          newSelection.delete(selectableId);
          areaSelectStore.setTmpSelection(newSelection);
          continue;
        }
      }
    },
    [container, areaSelectStore]
  );

  const handleMouseDown = React.useCallback(
    (e: MouseEvent) => {
      if (e.button == 2) {
        return;
      }
      const containerElement = container.current;

      if (!(e.target instanceof HTMLElement)) return;

      if (!e.target.classList.contains("area-select-start")) {
        return;
      }

      if (containerElement && containerElement.contains(e.target as HTMLElement)) {
        document.addEventListener("mousemove", handleMouseMove);
        areaSelectStore.setSelectionArea({
          start: {
            x: e.clientX,
            y: e.clientY,
          },
          end: {
            x: e.clientX,
            y: e.clientY,
          },
        });
      }
    },
    [container, handleMouseMove, areaSelectStore]
  );

  const handleMouseUp = React.useCallback(
    (e: MouseEvent) => {
      if (!(e.target instanceof HTMLElement)) return;
      if (e.target.classList.contains("area-select-no-clear")) return;
      areaSelectStore.setSelectionArea(null);
      document.body.style.userSelect = "initial";
      document.removeEventListener("mousemove", handleMouseMove);

      const { isAreaSelecting, tmpSelection, tmpDeselection, selection } = areaSelectStore;

      // When clicking elsewhere in area select
      if (!isAreaSelecting && e.button !== 2) {
        areaSelectStore.clearSelection();
      } else {
        // merge tmp into selection, filtering out deselection
        const newSelection = new Set([...selection, ...tmpSelection].filter((id) => !tmpDeselection.has(id)));
        areaSelectStore.setSelection(newSelection);
      }

      areaSelectStore.setIsAreaSelecting(false);

      const containerElement = container.current;
      const selectionBoxElement = boxElementRef.current;
      if (containerElement && selectionBoxElement) {
        if (containerElement.contains(selectionBoxElement)) {
          containerElement.removeChild(selectionBoxElement);
        }
      }
    },
    [handleMouseMove, container, areaSelectStore]
  );

  const containerElement = container.current;
  React.useEffect(() => {
    if (containerElement) {
      containerElement.addEventListener("mousedown", handleMouseDown);
      document.addEventListener("mouseup", handleMouseUp, { capture: true });

      return () => {
        containerElement.removeEventListener("mousedown", handleMouseDown);
        document.removeEventListener("mouseup", handleMouseUp, { capture: true });
      };
    }
  }, [containerElement, handleMouseDown, handleMouseUp]);

  React.useEffect(() => {
    if (!areaSelectStore.selectionArea) return;
    const { start, end } = areaSelectStore.selectionArea;
    if (start && end && boxElementRef.current) {
      drawSelectionBox(boxElementRef.current, start, end);
    }
  }, [boxElementRef, areaSelectStore.selectionArea]);
}

export function useSelectable(elementRef: React.RefObject<HTMLElement | null>, selectableIdentifier: SelectableId) {
  const { areaSelectStore } = useStores();
  const { selection, tmpSelection, tmpDeselection } = areaSelectStore;

  const isSelected =
    !tmpDeselection.has(selectableIdentifier) &&
    (selection.has(selectableIdentifier) || tmpSelection.has(selectableIdentifier));

  React.useEffect(() => {
    if (elementRef.current) {
      areaSelectStore.registerSelectable(selectableIdentifier, elementRef);
    }
    return () => {
      areaSelectStore.deregisterSelectable(selectableIdentifier);
    };
  }, [areaSelectStore, elementRef, selectableIdentifier]);

  return { isSelected };
}

function drawSelectionBox(boxElement: HTMLElement, start: Coordinates, end: Coordinates): void {
  const b = boxElement;
  if (end.x > start.x) {
    b.style.left = start.x + "px";
    b.style.width = end.x - start.x + "px";
  } else {
    b.style.left = end.x + "px";
    b.style.width = start.x - end.x + "px";
  }

  if (end.y > start.y) {
    b.style.top = start.y + "px";
    b.style.height = end.y - start.y + "px";
  } else {
    b.style.top = end.y + "px";
    b.style.height = start.y - end.y + "px";
  }
}
